import { NativeModules } from 'react-native';
import { PERMISSIONS, RESULTS, request } from 'react-native-permissions';

import { isAndroid, isIos } from '@/utils/constants-platform-specific';

const { AdvertisingModule, AndroidAdvertisingModule } = NativeModules;

// The OS returns this all-zeros UUID in place of the real advertising ID when ad tracking
// is denied/limited (Android with limit ad tracking on, iOS with ATT not granted).
const NO_ADVERTISING_ID = '00000000-0000-0000-0000-000000000000';

/**
 * Retrieves the Identifier for Advertisers (IDFA) from the iOS device.
 *
 * @returns {Promise<string | undefined>} A promise that resolves with the IDFA as a string if available, or rejects with 'IDFA not found' if not available.
 */
export async function getIdfa(): Promise<string | undefined> {
    return new Promise((resolve, reject) => {
        AdvertisingModule.getIDFA((idfa: string) => {
            if (idfa) {
                resolve(idfa);
            } else {
                reject('IOS IDFA not found. Ensure tracking is allowed for the app.');
            }
        });
    });
}

/**
 * Retrieves the Google Advertising ID (GAID) from the Android device.
 *
 * @returns {Promise<string>} A promise that resolves with the GAID as a string or rejects with an error.
 */
export function getGaid(): Promise<string> {
    return new Promise((resolve, reject) => {
        AndroidAdvertisingModule.getAdvertisingId()
            .then((gaid: string) => resolve(gaid))
            .catch((error: any) => reject(error));
    });
}

/**
 * Checks if ad tracking is enabled on the Android device.
 *
 * @returns {Promise<boolean>} A promise that resolves with a boolean indicating whether ad tracking is enabled.
 */
export function getAndroidIsAdTrackingEnabled(): Promise<boolean> {
    return new Promise((resolve, reject) => {
        AndroidAdvertisingModule.isLimitAdTrackingEnabled()
            .then((isAdTrackingEnabled: boolean) => resolve(isAdTrackingEnabled))
            .catch((error: any) => reject(error));
    });
}

/**
 * Checks whether the user permits ad tracking: ad tracking must be enabled on Android
 * and App Tracking Transparency must be granted on iOS. On iOS this shows the ATT
 * prompt when the permission is still undetermined and resolves with the user's
 * decision, so consent is not read before the user has answered.
 *
 * @returns {Promise<boolean>} A promise that resolves with whether ad tracking is permitted.
 */
export async function isAdTrackingPermitted(): Promise<boolean> {
    if (isAndroid) {
        return getAndroidIsAdTrackingEnabled();
    }
    if (isIos) {
        const attPermission = await request(PERMISSIONS.IOS.APP_TRACKING_TRANSPARENCY);
        return attPermission === RESULTS.GRANTED;
    }
    return false;
}

/**
 * Resolves the device advertising ID only when the user permits ad tracking. Returns
 * `undefined` when tracking is not permitted or the ID is a zeroed-out placeholder, so
 * callers can distinguish "usable ID" from "must not track".
 *
 * @returns {Promise<string | undefined>} A promise that resolves with the advertising ID, or `undefined` if it may not be used.
 */
export async function getPermittedAdvertisingId(): Promise<string | undefined> {
    if (!(await isAdTrackingPermitted())) {
        return undefined;
    }

    const advertisingId = isAndroid ? await getGaid() : await getIdfa();
    return advertisingId && advertisingId !== NO_ADVERTISING_ID ? advertisingId : undefined;
}
